/**
 * Subscription entitlement
 *
 * Single place that answers "is this account currently entitled to paid
 * features?" for both `SubscriptionContext` (feature paywall) and
 * `useBilling` (hard paywall), so the two can't drift.
 *
 * The rule that matters: entitlement follows the Stripe subscription status,
 * not the plan name. A canceled account keeps its `currentPlan: "Business"`
 * label and `planSource: "stripe_subscription"` server-side, so gating on the
 * plan name alone leaves paid features fully open after a cancel (SUPENG-210).
 *
 * Cancelling does not delete the customer's work. A previously-paid account
 * that goes terminal becomes **read-only**: the heatmaps, funnels, experiments
 * and attribution data they already paid to collect stay readable, but every
 * creation path is paywalled (`isReadOnly` drives `usePlanUsage`, which turns
 * every create CTA into an Upgrade). `hasActiveSubscription` still goes false.
 *
 * Stripe's Customer Portal cancels at period end by default: the status stays
 * `active`/`trialing` until the period actually lapses, so access survives
 * until then.
 */

import { getFeatureAccessForPlan } from '@/lib/constants/pricing'
import type { FeatureAccess, PlanTier, PlanUsageMetadata } from '@/lib/types/billing'

/**
 * True when the usage endpoint (`GET /subscription/:propertyId/usage`)
 * explicitly says this feature is off for the property's subscription — the
 * one client-visible signal that reflects the *owner's* real plan state, not
 * the cached `/property/all` blob.
 *
 * The client-side entitlement can't see a workspace owner's cancel (membership
 * is inferred from cached plan state that survives it), so billable actions —
 * AI sends, API key minting — must consult this on top of their local gates.
 * Deny only on an explicit `false`: a missing/loading/errored response is an
 * outage, and outages never gate (same fail-open policy as the usage counters).
 */
export function isServerFeatureDenied(
  metadata: PlanUsageMetadata | null,
  feature: keyof PlanUsageMetadata['features'],
): boolean {
  return metadata?.features?.[feature] === false
}

/**
 * Statuses where the subscription no longer entitles the account to anything.
 * `past_due` is deliberately absent — Stripe retries for days and the customer
 * is still paying us; dropping them to Free mid-dunning is its own churn bug.
 */
const TERMINAL_STATUSES = new Set([
  'canceled',
  'cancelled',
  'unpaid',
  'incomplete_expired',
])

export function isTerminalSubscriptionStatus(status?: string | null): boolean {
  if (!status) return false
  return TERMINAL_STATUSES.has(status.toLowerCase())
}

/**
 * Statuses where the customer has a real subscription with a payment problem.
 * These need the Stripe portal (the unpaid invoice lives there), not a new
 * checkout — `unpaid` is terminal for entitlement but is still a billing
 * relationship we want to recover, not a churned account starting over.
 */
const PAYMENT_RECOVERY_STATUSES = new Set(['past_due', 'unpaid'])

export function isPaymentRecoveryStatus(status?: string | null): boolean {
  if (!status) return false
  return PAYMENT_RECOVERY_STATUSES.has(status.toLowerCase())
}

/**
 * Upgrade CTAs open the Stripe portal when there is an open invoice to pay.
 * Needs a subscription id — without one the portal has nothing to show, and
 * the plan picker is the fallback. `canceled` is excluded: that sub can't be
 * revived in the portal and needs a new checkout.
 */
export function needsInvoicePortal(
  status?: string | null,
  stripeSubscriptionId?: string | null,
): boolean {
  return isPaymentRecoveryStatus(status) && !!stripeSubscriptionId
}

/**
 * Whether the account is in a trial right now.
 *
 * A terminal status voids any trial flag riding along on the cached
 * `/property/all` payload. Without this, revoking `hasPaidStripePlan` on cancel
 * hands the stale `subscription.isTrial` the win and the account flips to
 * "trialing" — which reopens every trial bypass downstream (`usePlanUsage`
 * reports all counters unlimited, so website and seat limits stop applying).
 */
export function resolveActiveTrial({
  isLocalTrial = false,
  stripeTrialing = false,
  hasPaidStripePlan = false,
  stripeStatus,
}: {
  isLocalTrial?: boolean
  stripeTrialing?: boolean
  hasPaidStripePlan?: boolean
  stripeStatus?: string | null
}): boolean {
  if (isTerminalSubscriptionStatus(stripeStatus)) return false
  // Only a local/onboarding trial reads as "trial" for plan-card UX — a Stripe
  // subscription still in its trial window is a paid Business/Scale plan.
  return isLocalTrial || (stripeTrialing && !hasPaidStripePlan)
}

/** Maps a plan display name ("Business", "Plus Legacy") to its tier. */
export function resolvePlanTier(planName?: string | null): PlanTier {
  const name = (planName || '').toLowerCase()
  if (name.includes('scale')) return 'scale'
  if (name.includes('business')) return 'business'
  if (name.includes('plus') && name.includes('legacy')) return 'plus_legacy'
  if (name.includes('plus')) return 'plus'
  return 'free'
}

/**
 * Workspace membership: a non-owner member of a property whose owner is on a
 * paid (non-local-trial) subscription inherits access from that team plan.
 *
 * This reads `/property/all`, which carries no per-property Stripe status —
 * `status: 'NORMAL'` is account state and `isFreePlan` is derived from the plan
 * name, both of which survive the owner's cancel. So this signal can say "the
 * owner is paying" after they've churned, and it must never be strong enough to
 * *grant* anything the caller's own billing status denies. Detecting an owner's
 * cancel on behalf of their members is server-side work: `usePlanUsage` reads
 * the authoritative per-account counters from
 * `GET /subscription/:propertyId/usage`, which is what actually stops a member
 * creating on a churned owner's property, and its `metadata.features` flags
 * (via `isServerFeatureDenied`) additionally close the billable actions that
 * don't ride a counter — AI sends and API key minting.
 */
export function propertyGrantsMembershipAccess(property?: {
  isOwner?: boolean
  subscription?: {
    status?: string
    isFreePlan?: boolean
    localTrialActive?: boolean
    planSource?: string
  }
}): boolean {
  if (!property?.subscription) return false
  if (property.isOwner !== false) return false

  const sub = property.subscription
  const isLocalTrial =
    sub.localTrialActive === true || sub.planSource === 'local_trial'
  if (isLocalTrial) return false

  return sub.status === 'NORMAL' && sub.isFreePlan === false
}

interface EntitlementInput {
  /** Plan display name from `/property/all` or `/billing/status`. */
  planName?: string | null
  /** `stripe_subscription` | `local_trial` | null. */
  planSource?: string | null
  /**
   * Stripe subscription status from `GET /billing/status`. Pass `null`/omit
   * when the caller never fetched it — the plan name plus trial flags are then
   * the only signal available, which is how `useBilling` has always worked.
   *
   * Caller contract: null means "never fetched" and **fails open** (a paid
   * plan name reads as live). A caller that *did* fetch and got an error must
   * not degrade to null — feed the last status it observed instead, or a
   * single failed refetch erases a canceled status and reopens every
   * `isReadOnly` gate off the stale paid plan name in `/property/all`.
   * `SubscriptionContext` keeps that snapshot in `lastBillingRef`.
   */
  stripeStatus?: string | null
  /** Local/onboarding trial, or a Stripe trial on a non-paid plan. */
  hasActiveTrial?: boolean
  /** Raw trial flags from `/property/all` (`isTrial`, `localTrialActive`). */
  isTrialFlagged?: boolean
  /**
   * Per-property feature overrides. Ignored once the status is terminal —
   * read-only access comes from the plan tier, not from a cached blob that
   * could grant more than the plan ever did.
   */
  propertyFeatures?: Partial<FeatureAccess> | null
  /** True when a workspace owner's paid plan covers this user. */
  hasMembershipAccess?: boolean
  /**
   * The account has spent its trial (`usedTrial`). With no live trial, no live
   * subscription and no team plan, that is a lapsed trial: view-only, the same
   * as a cancel, instead of a wall over its own data.
   */
  usedTrial?: boolean
}

/** Why an account is view-only, so copy can say "trial" or "subscription". */
export type ReadOnlyReason = 'canceled' | 'trial_ended'

/** Plan a lapsed trial is read against when the plan name fell back to Free. */
const LAPSED_TRIAL_TIER: PlanTier = 'business'

interface Entitlement {
  /**
   * Tier whose limits and labels the account is shown. Always the tier the plan
   * name implies: a read-only account keeps its old tier so its history renders
   * and the plan card still names what they had, and a workspace member keeps
   * their own tier — inherited access shows up in `features`, not here, so a
   * covered member isn't mislabelled as being on Scale themselves.
   */
  planTier: PlanTier
  /** True when the Stripe subscription is canceled/unpaid/expired. */
  isTerminal: boolean
  /**
   * Churned but previously paid: existing work stays readable while every
   * creation path is paywalled. Consumers must treat this as "view, don't
   * create" — `features` is deliberately still the paid set.
   */
  isReadOnly: boolean
  /** `canceled` after a paid plan went terminal, `trial_ended` for a lapsed trial. */
  readOnlyReason: ReadOnlyReason | null
  hasPaidStripePlan: boolean
  hasActiveSubscription: boolean
  features: FeatureAccess
}

/**
 * Resolves what an account can actually use right now.
 *
 * A churned account keeps read access to what it built and loses creation.
 * Workspace membership can widen `features` for a member in good standing, but
 * it can never override the caller's own terminal status — see
 * `propertyGrantsMembershipAccess` for why that signal isn't trustworthy enough.
 */
export function resolveEntitlement(input: EntitlementInput): Entitlement {
  const {
    planName,
    planSource,
    stripeStatus,
    hasActiveTrial = false,
    isTrialFlagged = false,
    propertyFeatures,
    hasMembershipAccess = false,
    usedTrial = false,
  } = input

  const planTier = resolvePlanTier(planName)
  const normalizedStatus = stripeStatus ? stripeStatus.toLowerCase() : null
  const isTerminal = isTerminalSubscriptionStatus(normalizedStatus)

  const hasPaidPlanName = !!planName && planName.toLowerCase() !== 'free'
  const hasPaidStripePlan =
    !isTerminal && planTier !== 'free' && planSource === 'stripe_subscription'

  // Without a Stripe status the plan name plus trial flags are the only
  // signal — an unknown status is not a terminal one, so this stays fail-open.
  const paidWithoutTrial =
    !hasActiveTrial &&
    !isTrialFlagged &&
    (normalizedStatus === 'active' ||
      planSource === 'stripe_subscription' ||
      normalizedStatus === null)

  const hasActiveSubscription =
    !isTerminal && hasPaidPlanName && (hasPaidStripePlan || paidWithoutTrial)

  // Read-only needs a paid plan name to fall back on; an account that went
  // terminal without one has no paid history to preserve and is simply free.
  //
  // Membership deliberately does *not* exempt anyone here. It's inferred from
  // cached plan state that survives the owner's cancel, so letting it suppress
  // the caller's own terminal status would reopen the bypass this whole module
  // exists to close — a churned owner who happens to sit in someone's workspace
  // would keep creating. Erring toward read-only costs a covered member their
  // create buttons until their own billing is sorted; erring the other way
  // costs revenue.
  const isCanceledReadOnly = isTerminal && planTier !== 'free'

  // A spent trial with nothing live behind it keeps its history the same way.
  // Membership does exempt this one: a covered member has a live team plan, and
  // a lapsed trial has no terminal status for membership to be hiding.
  const isLapsedTrial =
    !isCanceledReadOnly &&
    usedTrial &&
    !hasActiveTrial &&
    !hasActiveSubscription &&
    !hasMembershipAccess

  const isReadOnly = isCanceledReadOnly || isLapsedTrial
  const readOnlyReason: ReadOnlyReason | null = isCanceledReadOnly
    ? 'canceled'
    : isLapsedTrial
      ? 'trial_ended'
      : null

  const features: FeatureAccess = isReadOnly
    ? // Read access to what they already paid for (or trialed). Creation is
      // blocked by `isReadOnly` downstream, not by stripping the feature flags —
      // the flags are what make the history render at all. Both
      // `propertyFeatures` and membership are ignored: neither may out-grant the
      // ended plan. A lapsed trial whose plan name fell back to Free is read
      // against the plan it trialed.
      getFeatureAccessForPlan(isLapsedTrial && planTier === 'free' ? LAPSED_TRIAL_TIER : planTier)
    : hasMembershipAccess
      ? getFeatureAccessForPlan('scale')
      : isTerminal
        ? getFeatureAccessForPlan('free')
        : { ...getFeatureAccessForPlan(planTier), ...propertyFeatures }

  return {
    planTier,
    isTerminal,
    isReadOnly,
    readOnlyReason,
    hasPaidStripePlan,
    hasActiveSubscription,
    features,
  }
}
